import { Flex } from '@chakra-ui/react';
import { getNodeAttributes } from '@tiptap/core';
import { TEXT_ALIGNMENT } from 'constants/fonts';
import { TEXT_EDITED, TEXT_TAG_EDITED } from 'constants/mixpanel';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { Bold, Italic, Underline } from 'modules/common/components/FontStyle';
import { AddLinkButton } from 'modules/common/components/Link';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { useCallback } from 'react';
import { preventDefault } from 'utils/event';
import { ModifyFontSizeOperation } from 'widgets/TextBasedWidgets/common/extensions/fontSize';
import { useWidget } from 'widgets/sdk';

import { TextWidgetTag } from '../../common/TextBasedWidgets.types';
import {
  FontFamily,
  FontSize,
  LineHeight,
  OrderedList,
  TextAlignment,
  TextColor,
  UnOrderedList,
} from '../../common/toolbar';
import { TEXT_WIDGET_TAG_OPTIONS } from '../TextWidget.config';
import { findFirstColor, replaceTextContent } from '../TextWidget.helpers';
import { TextWidgetData } from '../TextWidget.types';
import { Effects } from './Effects';
import { ImproveText } from './ImproveText';
import { LetterSpacing } from './LetterSpacing';
import { TextTag } from './TextTag';
import { TextWidgetToolbarProps } from './TextWidgetToolbar.types';

export const TextWidgetToolbar = ({
  editor,
  isEditable,
  isTypingRef,
  textWrapperRef,
  setWidgetState,
  dispatchUpdateWidget,
}: TextWidgetToolbarProps) => {
  const { textTag } = useWidget<TextWidgetData>();

  const debounceDispatchUpdateWidget = useDebouncedCallback(dispatchUpdateWidget, 200);
  const textStyle = editor?.getAttributes('textStyle');

  // if the widget is active / default mode the editor has to be focused to update the style
  const handleEditorFocus = useCallback(() => {
    isTypingRef.current = false;
    if (!isEditable) editor?.commands.focus('all');
  }, [editor, isEditable, isTypingRef]);

  // Update the editor, handle focus and dispatch the widget update
  const onUpdateEditor = useCallback(
    (updateEditorFn: () => void) => {
      handleEditorFocus();

      // the height of the widget before the update
      const previousHeight = editor?.view.dom.clientHeight ?? 0;

      // e.g. editor.chain().focus().toggleBold().run()
      updateEditorFn();

      dispatchUpdateWidget(undefined, undefined, previousHeight);
    },
    [editor, dispatchUpdateWidget, handleEditorFocus],
  );

  // Get node attributes based on tag type
  const nodeAttributes = getNodeAttributes(editor.state, editor.schema.nodes.paragraph);

  const isBold = !!editor?.isActive('bold');
  const isItalic = !!editor?.isActive('italic');
  const isUnderline = !!editor?.isActive('underline');
  const isLink = !!editor?.isActive('link');
  const fontFamily = textStyle?.fontFamily;
  const fontSize = String(parseFloat(textStyle?.fontSize) ?? '');
  const color = textStyle?.color;
  const textAlign = nodeAttributes?.textAlign;
  const lineHeight = nodeAttributes?.lineHeight;
  const isOrderedList = !!editor?.isActive('orderedList');
  const isUnOrderedList = !!editor?.isActive('bulletList');
  const link = editor?.getAttributes('link')?.href || '';

  const onSetTextTag = useCallback(
    (newTextTag: TextWidgetTag) => {
      if (newTextTag === textTag) return;

      // The prosemirrorData has not been updated so we need to force this to update the widgetData
      dispatchUpdateWidget({ textTag: newTextTag }, true);

      const selectedTagIndex = TEXT_WIDGET_TAG_OPTIONS.findIndex((opt) => opt.value === newTextTag);
      const text_tag = TEXT_WIDGET_TAG_OPTIONS[selectedTagIndex].label;
      Mixpanel.track(TEXT_TAG_EDITED, { from: 'Text Toolbar', text_tag });
    },
    [dispatchUpdateWidget, textTag],
  );

  const onSetBold = useCallback(() => {
    onUpdateEditor(() => editor.chain().focus().toggleBold().run());
  }, [editor, onUpdateEditor]);

  const onSetItalic = useCallback(() => {
    onUpdateEditor(() => editor.chain().focus().toggleItalic().run());
  }, [editor, onUpdateEditor]);

  const onSetUnderline = useCallback(() => {
    onUpdateEditor(() => editor.chain().focus().toggleUnderline().run());
  }, [editor, onUpdateEditor]);

  const onSetFontFamily = useCallback(
    (fontFamily: string) => {
      if (!fontFamily) return;
      onUpdateEditor(() => editor.chain().focus().setFontFamily(fontFamily).run());
      Mixpanel.track(TEXT_EDITED.eventName, { styling_property: TEXT_EDITED.styling_property.Font });
    },
    [editor, onUpdateEditor],
  );

  const onSetFontColor = useCallback(
    (color: string) => {
      handleEditorFocus();
      editor.chain().focus().setColor(color).run();
      debounceDispatchUpdateWidget();
    },
    [editor, debounceDispatchUpdateWidget, handleEditorFocus],
  );

  const onSetFontSize = useCallback(
    (newFontSize: number) => {
      if (!newFontSize) return;
      onUpdateEditor(() => editor.chain().focus().setFontSize(`${newFontSize}px`).run());
    },
    [editor, onUpdateEditor],
  );

  const onIncrementFontSize = useCallback(() => {
    onUpdateEditor(() => editor.chain().focus().modifyFontSizeByOperation(ModifyFontSizeOperation.Increment).run());
  }, [onUpdateEditor, editor]);

  const onDecrementFontSize = useCallback(() => {
    onUpdateEditor(() => editor.chain().focus().modifyFontSizeByOperation(ModifyFontSizeOperation.Decrement).run());
  }, [onUpdateEditor, editor]);

  const onSetTextAlign = useCallback(
    (alignment: TEXT_ALIGNMENT) => {
      if (!alignment) return;
      onUpdateEditor(() => editor.chain().focus().setTextAlign(alignment).run());
    },
    [editor, onUpdateEditor],
  );

  const onSetLineHeight = useCallback(
    (lineHeight: string) => {
      if (!lineHeight) return;
      onUpdateEditor(() => editor.chain().focus().setLineHeight(lineHeight).run());
    },
    [editor, onUpdateEditor],
  );

  const onSetOrderedList = useCallback(() => {
    // Toggle list ON will convert widget to <p/>
    if (!editor.isActive('orderedList') && textTag !== TextWidgetTag.Paragraph) {
      onSetTextTag(TextWidgetTag.Paragraph);
    }

    onUpdateEditor(() => editor.chain().focus().toggleOrderedList().run());
  }, [editor, textTag, onUpdateEditor, onSetTextTag]);

  const onSetUnorderedList = useCallback(() => {
    // Toggle list ON will convert widget to <p/>
    if (!editor.isActive('bulletList') && textTag !== TextWidgetTag.Paragraph) {
      onSetTextTag(TextWidgetTag.Paragraph);
    }

    onUpdateEditor(() => editor.chain().focus().toggleBulletList().run());
  }, [editor, textTag, onUpdateEditor, onSetTextTag]);

  const onSetLink = useCallback(
    (newUrl: string | null) => {
      if (newUrl === null) return;

      if (newUrl === '') {
        if (!editor?.isFocused) editor?.commands.focus('all');
        onUpdateEditor(() =>
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .unsetLink()
            .command(({ commands }) => {
              if (isUnderline) {
                return commands.toggleUnderline();
              }
              return false;
            })
            .run(),
        );

        return;
      }

      // Set link with protocol
      onUpdateEditor(() =>
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: newUrl })
          .command(({ commands }) => {
            if (!isUnderline) {
              return commands.toggleUnderline();
            }
            return false;
          })
          .run(),
      );
    },
    [editor, onUpdateEditor, isUnderline],
  );

  // return focus back to selection
  const focusEditor = useCallback(() => {
    if (isEditable) editor.commands.focus();
  }, [isEditable, editor]);

  // stop click events from propagation to the text widget itself
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation();

  const getTextColor = useCallback(() => {
    const color = findFirstColor(editor);
    return color;
  }, [editor]);

  const onReplaceTextContent = useCallback(
    (text: string) => {
      onUpdateEditor(() => replaceTextContent(editor, text));
    },
    [editor, onUpdateEditor],
  );

  return (
    <Flex onClick={onClick} mr='8px' gap='8px' align='center'>
      <TextColor color={color} onChange={onSetFontColor} onClose={focusEditor} />
      <FontFamily value={fontFamily} onChange={onSetFontFamily} onClose={focusEditor} />
      <FontSize
        value={fontSize}
        onChange={onSetFontSize}
        onDecrement={onDecrementFontSize}
        onIncrement={onIncrementFontSize}
      />
      <Bold fontFamily={fontFamily} isActive={isBold} onChange={onSetBold} />
      <Italic fontFamily={fontFamily} isActive={isItalic} onChange={onSetItalic} />
      <Underline isActive={isUnderline} onChange={onSetUnderline} />
      <ToolbarDivider />
      <TextAlignment value={textAlign} onChange={onSetTextAlign} />
      <LineHeight value={lineHeight} onChange={onSetLineHeight} />
      <LetterSpacing textWrapperRef={textWrapperRef} isTypingRef={isTypingRef} />
      <ToolbarDivider />
      <UnOrderedList isActive={isUnOrderedList} onChange={onSetUnorderedList} />
      <OrderedList isActive={isOrderedList} onChange={onSetOrderedList} />
      <ToolbarDivider />
      <AddLinkButton value={link} isActive={isLink} onChange={onSetLink} onMouseDown={preventDefault} />
      <TextTag value={textTag || TextWidgetTag.Paragraph} onChange={onSetTextTag} onClose={focusEditor} />
      <ImproveText editor={editor} onReplaceTextContent={onReplaceTextContent} setWidgetState={setWidgetState} />
      <ToolbarDivider />
      <Effects getTextColor={getTextColor} />
    </Flex>
  );
};
